import { FC, ReactNode } from 'react';
import { TooltipProps } from 'recharts';

import styles from './PunchupChartTooltip.module.css';

type ChartTooltipEntry = NonNullable<TooltipProps<number, string>['payload']>[number];

export interface PunchupChartTooltipProps extends TooltipProps<number, string> {
	/** Renders the header line from the recharts `label` (default: the raw label). */
	formatHeader?: (label: unknown) => ReactNode;
	/** Formats each row's numeric value (default: the raw value). */
	formatValue?: (value: number) => ReactNode;
	/** The label shown before a row's value (default: the series name). */
	formatRowLabel?: (entry: ChartTooltipEntry) => ReactNode;
}

const identityHeader = (label: unknown): ReactNode => label as ReactNode;
const rawValue = (value: number): ReactNode => value;
const seriesName = (entry: ChartTooltipEntry): ReactNode => entry.name;

/**
 * Shared recharts custom-tooltip for the Punchup analytics charts: a header line plus one
 * swatch + label + value row per non-zero series. Each chart passes only its variation — the
 * header format (a raw day label vs. a formatted date), the value format (raw count vs.
 * currency), and the row label (the series name vs. a fixed caption) — while the markup and the
 * tokenised styling live here, so the Sales-Trends tooltips can't drift apart. See ../CLAUDE.md.
 */
export const PunchupChartTooltip: FC<PunchupChartTooltipProps> = ({
	active,
	payload,
	label,
	formatHeader = identityHeader,
	formatValue = rawValue,
	formatRowLabel = seriesName,
}) => {
	if (!active || !payload?.length) return null;
	return (
		<div className={styles.tooltip}>
			<div className={styles.tooltipHeader}>{formatHeader(label)}</div>
			{payload
				.filter((entry) => (entry.value ?? 0) > 0)
				.map((entry) => (
					<div key={String(entry.name)} className={styles.tooltipRow}>
						<span className={styles.tooltipSwatch} style={{ background: entry.color }} />
						{formatRowLabel(entry)}: {formatValue(entry.value as number)}
					</div>
				))}
		</div>
	);
};
